/**
 * Swatch page filters: attribute matchers, product→collection option sync,
 * browse vs filtered modes, category strips, jump links, and product deep link.
 */
import { createSharedFilterBar } from './shared-filter-bar';
import { createSwatchGridRenderer } from './swatch-grid-render';

const LG_BREAKPOINT = 992;
const FILTER_GROUP_KEYS = ['color', 'solution', 'product', 'collection'];

const createSwatchFilters = (ctx) => {
  const {
    app,
    state,
    browseMode,
    filteredMode,
    filteredCards,
    emptyState,
    visualizeTileTemplate,
    visualizeUrl,
  } = ctx;

  const isDesktopLg = () => window.innerWidth >= LG_BREAKPOINT;

  const hasActiveFilters = filters => (
    FILTER_GROUP_KEYS.some(key => ((filters && filters[key]) || []).length > 0)
  );

  // Matching runs off the catalog data island when it is present and falls back to reading the
  // cards when it is not. The two are kept behaviourally identical - tools/swatch-filter-parity
  // compares them across filter combinations - so this can be removed once the server stops
  // rendering the grid at all.
  const catalog = ctx.catalog || null;

  const cardAttr = (card, name) => String(card.getAttribute(name) || '').trim();

  const splitIds = value => String(value || '')
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);

  /** The subject every matcher below reads, built from a record or from a card. */
  const subjectForCard = card => (catalog && catalog.get(cardAttr(card, 'data-swatch-id'))) || {
    productLineId: cardAttr(card, 'data-productline'),
    collection: cardAttr(card, 'data-collection'),
    colorRangeIds: splitIds(card.getAttribute('data-colorranges')),
    solutionIds: splitIds(card.getAttribute('data-solutions')),
  };

  const buildProductCollectionMap = () => {
    const map = {};

    const add = (lineId, collection) => {
      if (!lineId || !collection) {
        return;
      }
      if (!map[lineId]) {
        map[lineId] = new Set();
      }
      map[lineId].add(collection);
    };

    if (catalog) {
      catalog.records.forEach(r => add(r.productLineId, r.collection));
      return map;
    }

    filteredCards.forEach((card) => {
      add(cardAttr(card, 'data-productline'), cardAttr(card, 'data-collection'));
    });

    return map;
  };

  const productCollectionMap = buildProductCollectionMap();

  const getAllowedCollections = (products) => {
    if (!products || products.length === 0) {
      return null;
    }

    const allowed = new Set();
    products.forEach((id) => {
      const collections = productCollectionMap[String(id)];
      if (!collections) {
        return;
      }
      collections.forEach((name) => {
        allowed.add(name);
      });
    });

    return allowed;
  };

  const syncCollectionOptions = (products, options) => {
    if (!ctx.filterBar || typeof ctx.filterBar.setOptionVisibility !== 'function') {
      return null;
    }
    return ctx.filterBar.setOptionVisibility(
      'collection',
      getAllowedCollections(products),
      options,
    );
  };

  const matchesProduct = (subject, products) => {
    if (!products || products.length === 0) {
      return true;
    }
    return products.some(id => String(id) === subject.productLineId);
  };

  const matchesSolution = (subject, solutions) => {
    if (!solutions || solutions.length === 0) {
      return true;
    }
    return solutions.some(id => subject.solutionIds.indexOf(String(id)) !== -1);
  };

  const matchesCollection = (subject, collections) => {
    if (!collections || collections.length === 0) {
      return true;
    }
    return collections.indexOf(subject.collection) !== -1;
  };

  const matchesColor = (subject, colors) => {
    if (!colors || colors.length === 0) {
      return true;
    }
    return colors.some(id => subject.colorRangeIds.indexOf(String(id)) !== -1);
  };

  const subjectMatchesFilters = (subject, filters) => (
    matchesColor(subject, filters.color)
    && matchesSolution(subject, filters.solution)
    && matchesProduct(subject, filters.product)
    && matchesCollection(subject, filters.collection)
  );

  const cardMatchesFilters = (card, filters) => (
    subjectMatchesFilters(subjectForCard(card), filters)
  );

  // Still returns cards, not records. swatch-detail.js consumes this to drive prev/next through
  // the filtered set, and the My Account favorites page supplies its own card-based version, so
  // the contract has to stay the same until the grid itself is built from the catalog.
  const getMatchingFilteredCards = () => (
    filteredCards.filter(card => cardMatchesFilters(card, state.filters))
  );

  const getStripColumnCount = (strip) => {
    if (!strip) {
      return 3;
    }

    const sample = strip.querySelector('.swatch-card-col') || strip.querySelector('.swatch-see-all');
    if (!sample) {
      return 3;
    }

    const wasHidden = sample.classList.contains('d-none');
    if (wasHidden) {
      sample.classList.remove('d-none');
    }

    const stripStyle = window.getComputedStyle(strip);
    const gap = parseFloat(stripStyle.columnGap || stripStyle.gap || '0') || 0;
    const stripWidth = strip.clientWidth;
    const sampleWidth = sample.getBoundingClientRect().width;

    if (wasHidden) {
      sample.classList.add('d-none');
    }

    if (!sampleWidth || !stripWidth) {
      return 3;
    }

    return Math.max(1, Math.round((stripWidth + gap) / (sampleWidth + gap)));
  };

  const updateCategoryStrips = () => {
    if (!browseMode) {
      return;
    }

    Array.prototype.slice.call(browseMode.querySelectorAll('.swatch-category-strip')).forEach((strip) => {
      const cards = Array.prototype.slice.call(strip.querySelectorAll('.swatch-card-col'));
      const seeAll = strip.querySelector('.swatch-see-all');
      const totalCount = parseInt(strip.getAttribute('data-total-count') || '0', 10)
        || cards.length;
      const cols = getStripColumnCount(strip);

      if (totalCount <= cols) {
        cards.forEach((card) => {
          card.classList.remove('d-none');
        });
        if (seeAll) {
          seeAll.classList.add('d-none');
        }
        return;
      }

      const visibleCards = Math.max(0, cols - 1);
      cards.forEach((card, index) => {
        card.classList.toggle('d-none', index >= visibleCards);
      });
      if (seeAll) {
        seeAll.classList.remove('d-none');
      }
    });
  };

  const setMode = (mode) => {
    state.mode = mode;
    const isFiltered = mode === 'filtered';

    if (browseMode) {
      browseMode.classList.toggle('d-none', isFiltered);
    }
    if (filteredMode) {
      filteredMode.classList.toggle('d-none', !isFiltered);
    }

    const jumpNav = app.querySelector('.swatch-jump-nav');
    if (jumpNav) {
      jumpNav.classList.toggle('d-none', isFiltered);
    }
  };

  // Built after appendVisualizeTileIfNeeded below, which it calls back into once a group's last
  // card is in place.
  let gridRenderer = null;

  const removeVisualizeTiles = () => {
    if (!filteredMode) {
      return;
    }
    Array.prototype.slice.call(filteredMode.querySelectorAll('.swatch-visualize-tile')).forEach((el) => {
      el.remove();
    });
  };

  const appendVisualizeTileIfNeeded = (grid, visibleCount, productVisualizeUrl) => {
    if (!isDesktopLg() || !grid || visibleCount === 0) {
      return;
    }

    const href = (productVisualizeUrl || '').trim() || visualizeUrl || '#';

    let tile = null;
    if (visualizeTileTemplate) {
      const source = visualizeTileTemplate.querySelector('.swatch-visualize-tile')
        || visualizeTileTemplate.firstElementChild;
      if (source) {
        tile = source.cloneNode(true);
      }
    }

    if (!tile) {
      tile = document.createElement('div');
      tile.className = 'col swatch-visualize-tile';
      tile.innerHTML = [
        '<a href="', href, '" class="swatch-visualize-cta d-flex align-items-center justify-content-center h-100 w-100 text-center text-decoration-underline bg-teal-25">',
        '<span class="fw-semibold text-body">Visualize Your Space</span>',
        '</a>',
      ].join('');
    }

    tile.classList.remove('d-none');

    const cta = tile.querySelector('.swatch-visualize-cta');
    if (cta) {
      cta.setAttribute('href', href);
    }

    grid.appendChild(tile);
  };

  gridRenderer = createSwatchGridRenderer({
    filteredMode,
    filteredCards,
    favorites: ctx.favorites || {},
    appendVisualizeTile: (grid, count, group) => {
      appendVisualizeTileIfNeeded(grid, count, group.getAttribute('data-visualize-url'));
    },
    onChunkRendered: () => {
      // Cart membership is applied from localStorage, not server-rendered, so cards arriving in
      // a later chunk start out looking empty-handed. Resolved lazily rather than destructured:
      // createSwatchCartFavorites runs after this module, so ctx.initCartState does not exist
      // yet at construction, only by the time a filter can be applied.
      if (typeof ctx.initCartState === 'function') {
        ctx.initCartState();
      }
    },
  });

  /**
   * @param options.preserveRendered keep the cards already rendered and only redo the parts that
   *   depend on viewport width. Used by the resize handler, which needs the visualize tiles
   *   re-placed but must not reset the visitor to the first chunk.
   */
  const applyFilteredView = (options) => {
    const preserveRendered = !!(options && options.preserveRendered === true);

    removeVisualizeTiles();

    const groups = filteredMode
      ? Array.prototype.slice.call(filteredMode.querySelectorAll('.swatch-filtered-group'))
      : [];

    // Counts and group visibility come from the records, not from counting rendered cards: with
    // chunked rendering most matching cards do not exist in the DOM yet, so "(12)" would read as
    // however many happened to be rendered so far.
    const recordsByGroup = groups.map(() => []);
    let matchCount = 0;

    if (gridRenderer && catalog) {
      catalog.records.forEach((record) => {
        if (!subjectMatchesFilters(record, state.filters)) {
          return;
        }
        matchCount += 1;
        const bucket = recordsByGroup[record.groupIndex];
        if (bucket) {
          bucket.push(record);
        }
      });
    }

    groups.forEach((group, index) => {
      const count = recordsByGroup[index].length;
      const countEl = group.querySelector('.swatch-category-count');
      if (countEl) {
        countEl.textContent = `(${count})`;
      }
      group.classList.toggle('d-none', count === 0);
    });

    // Visualize tiles are appended by the renderer as each group completes, since they sit after
    // the group's last card.
    if (preserveRendered) {
      gridRenderer.refreshVisualizeTiles();
    } else {
      gridRenderer.render(recordsByGroup);
    }

    if (emptyState) {
      emptyState.classList.toggle('d-none', matchCount !== 0);
    }

    // The grid and the empty state swap without any announcement otherwise. Announces the total
    // match count rather than how many are rendered so far, which is what the visitor is asking
    // about and stays stable as chunks arrive.
    const statusRegion = app.querySelector('#swatch-filter-status');
    if (statusRegion) {
      statusRegion.textContent = matchCount === 0
        ? 'No swatches match the selected filters.'
        : `${matchCount} ${matchCount === 1 ? 'swatch' : 'swatches'} match the selected filters.`;
    }
  };

  const onFiltersChange = (filters) => {
    let nextFilters = {
      color: (filters && filters.color) || [],
      solution: (filters && filters.solution) || [],
      product: (filters && filters.product) || [],
      collection: (filters && filters.collection) || [],
    };

    const syncResult = syncCollectionOptions(nextFilters.product);
    if (syncResult && syncResult.filters) {
      nextFilters = {
        color: syncResult.filters.color || [],
        solution: syncResult.filters.solution || [],
        product: syncResult.filters.product || [],
        collection: syncResult.filters.collection || [],
      };
    }

    state.filters = nextFilters;

    if (hasActiveFilters(state.filters)) {
      setMode('filtered');
      applyFilteredView();
    } else {
      removeVisualizeTiles();
      setMode('browse');
      // Strips can only be measured once browse mode is visible again.
      updateCategoryStrips();
    }
  };

  const setJumpLinkActive = (tabKey) => {
    if (!tabKey) {
      return;
    }
    state.activeJump = tabKey;
    Array.prototype.slice.call(app.querySelectorAll('.swatch-jump-link')).forEach((link) => {
      const isActive = (link.getAttribute('data-tab') || '') === tabKey;
      link.classList.toggle('is-active', isActive);
      // is-active is a colour change only, so the current group is not exposed without this.
      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const syncJumpLinkFromScroll = () => {
    if (isDesktopLg() || state.mode === 'filtered') {
      return;
    }

    const sections = Array.prototype.slice.call(app.querySelectorAll('.swatch-tab-section'));
    if (sections.length === 0) {
      return;
    }

    const toolbar = app.querySelector('.swatch-toolbar');
    const headerHeight = parseFloat(document.documentElement.style.getPropertyValue('--header-height')) || 60;
    const toolbarHeight = toolbar ? toolbar.offsetHeight : 0;
    const probeY = headerHeight + toolbarHeight + 8;

    let activeKey = sections[0].getAttribute('data-tab') || sections[0].id;
    sections.forEach((section) => {
      if (section.getBoundingClientRect().top <= probeY) {
        activeKey = section.getAttribute('data-tab') || section.id;
      }
    });

    if (activeKey && activeKey !== state.activeJump) {
      setJumpLinkActive(activeKey);
    }
  };

  const resolveProductLineIdFromSelection = (selectedProduct) => {
    const isNumericId = /^\d+$/.test(selectedProduct);
    const selectedName = selectedProduct.toLowerCase();

    const rows = Array.prototype.slice.call(app.querySelectorAll('.swatch-category-row, .swatch-card-col'));

    for (let i = 0; i < rows.length; i += 1) {
      const el = rows[i];
      const lineId = el.getAttribute('data-productline-id')
        || el.getAttribute('data-productline')
        || '';
      const lineName = (
        el.getAttribute('data-productline-name')
        || ''
      ).trim().toLowerCase();

      if (isNumericId && String(lineId) === selectedProduct) {
        return String(lineId);
      }
      if (!isNumericId && lineName === selectedName) {
        return String(lineId);
      }
    }

    return null;
  };

  const applyProductDeepLink = () => {
    if (!ctx.filterBar) {
      return;
    }

    const current = ctx.filterBar.getState();
    if ((current.product || []).length > 0) {
      return;
    }

    const selectedProduct = (
      app.getAttribute('data-selected-product')
      || ''
    ).trim();
    if (!selectedProduct) {
      return;
    }

    const productLineId = resolveProductLineIdFromSelection(selectedProduct);
    if (!productLineId) {
      return;
    }

    ctx.filterBar.setState({
      color: (current.color || []).slice(),
      solution: (current.solution || []).slice(),
      product: [productLineId],
      collection: (current.collection || []).slice(),
    });
  };

  const initFilterBar = () => {
    ctx.filterBar = createSharedFilterBar({
      idPrefix: 'swatch',
      groupKeys: FILTER_GROUP_KEYS,
      urlSync: true,
      onFiltersChange,
      // Mobile defers Apply; refresh collection options from pending product picks.
      onMobilePendingChange: (pending) => {
        syncCollectionOptions((pending && pending.product) || [], { pendingOnly: true });
      },
    });

    // Init emit runs before filterBar is assigned on ctx; re-apply for collection sync.
    onFiltersChange(ctx.filterBar.getState());
    applyProductDeepLink();
    return ctx.filterBar;
  };

  return {
    hasActiveFilters,
    getMatchingFilteredCards,
    // Matching for an arbitrary filter set without disturbing the active one, so
    // tools/swatch-filter-parity can sweep combinations against its own DOM-based reference.
    matchingIdsForFilters: filters => filteredCards
      .filter(card => cardMatchesFilters(card, filters))
      .map(card => card.getAttribute('data-swatch-id')),
    ensureCardRendered: swatchId => gridRenderer.ensureCardRendered(swatchId),
    renderMoreCards: () => gridRenderer.renderMoreCards(),
    renderAllRemaining: () => gridRenderer.renderAllRemaining(),
    hasMoreToRender: () => gridRenderer.hasMoreToRender(),
    initFilterBar,
    applyFilteredView,
    updateCategoryStrips,
    setJumpLinkActive,
    syncJumpLinkFromScroll,
  };
};

export default createSwatchFilters;
export { createSwatchFilters };
